/*
 * Client-side hook to resolve SEO URLs to category IDs
 * Resolves categoryId from SEO URL parameters when not in SSR
 */

import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Resolves SEO URL segment to category ID using Commerce Cloud Shopper SEO API
 */
async function resolveCategoryId(brandId, categoryPath, accessToken, appOrigin) {
    const urlSegment = `${brandId}/${categoryPath}`
    const config = getConfig()

    // Use same configuration as SSR
    const proxyPath = config.app.commerceAPI.proxyPath
    const organizationId = config.app.commerceAPI.parameters.organizationId
    const siteId = config.app.commerceAPI.parameters.siteId

    const url = `${appOrigin}${proxyPath}/site/shopper-seo/v1/organizations/${organizationId}/url-mapping?siteId=${siteId}&urlSegment=${encodeURIComponent(urlSegment)}`

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
        }
    })

    if (!response.ok) {
        throw new Error(`Failed to resolve SEO URL: ${response.status}`)
    }

    const data = await response.json()

    if (data.resourceType === 'category' && data.resourceId) {
        return data.resourceId
    }

    return null
}

/**
 * Hook to resolve categoryId from SEO URL parameters
 * Only runs when we have SEO URL params and no server-side data
 */
export const useClientCategoryId = (
    brandId,
    categoryPath,
    skipClientResolve = false
) => {
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const shouldRun = !!(brandId && categoryPath && !skipClientResolve)

    return useQuery({
        queryKey: ['client-category-id', brandId, categoryPath],
        queryFn: async () => {
            const accessToken = await getTokenWhenReady()
            return resolveCategoryId(brandId, categoryPath, accessToken, appOrigin)
        },
        enabled: shouldRun,
        staleTime: 0, // No cache - always fresh request
        cacheTime: 0,
        retry: 1
    })
}
